/**
 * UpgradesPanel — магазин апгрейдов (doc 14).
 *
 * Sticky кнопка в нижнем левом углу с affordable бэйджем (cream + gold
 * кружки с числами). При тапе — bottom-sheet с табами по целевой
 * механике: тап / ритм / память / проводники. Внутри активного таба —
 * карточки апгрейдов (unlocked first, sorted by epoch+cost).
 *
 * Каждая карточка показывает:
 *   - название + краткое описание
 *   - текущий уровень / максимум (если ≥1)
 *   - цена следующего уровня + валюта
 *   - кнопка «купить» (disabled при недостатке валюты)
 *
 * Покупка → Zustand `purchaseUpgrade(id)`. На успех — звук + артефакт
 * (если зарегистрирован для этого uplevel). На отказ — мягкая визуальная
 * деактивация (без алерта).
 *
 * Wire site: SparkScreen footer. Высота кнопки ~36px, панель ~70vh.
 */
import { useMemo, useState } from 'react';
import { UPGRADES_ALPHA } from '@sai-clicker/content';
import {
  computeUpgradeCost,
  describeUpgradeEffect,
  getUpgradeLevel,
  isUpgradeUnlocked,
  type Epoch,
  type Upgrade,
} from '@sai-clicker/game-core';
import { useAnchor } from '../onboarding/useAnchor.js';
import { useUpgradesUiStore } from '../screens/upgradesUi.js';
import { useGameStore } from '../stores/game.js';
import { emit } from '../telemetry/emit.js';
import { formatNumber } from '../utils/format.js';
import styles from './UpgradesPanel.module.css';

export function UpgradesPanel(): JSX.Element {
  // JB 2026-05-26: open-state поднят в общий стор чтобы OnboardingDriver
  // мог закрывать панель программно при активной подсказке.
  const open = useUpgradesUiStore((s) => s.open);
  const setOpen = useUpgradesUiStore((s) => s.setOpen);
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  // Опенер регистрируется как onboarding anchor — подсказка про апгрейды
  // указывает в реальную кнопку в углу экрана.
  const openerAnchor = useAnchor<HTMLButtonElement>('upgrades');

  // JB 2026-05-26: бэйдж сколько апгрейдов сейчас можно купить.
  // Разделяем по валютам — осознания (warm cream) и духобаллы (gold).
  const purchased = useGameStore((s) => s.state.purchasedUpgrades);
  const epoch = useGameStore((s) => s.state.epoch);
  const awareness = useGameStore((s) => s.state.resources.awarenesses);
  const spiritBalls = useGameStore((s) => s.state.resources.spirit_balls);
  // 2026-05-26 perf: memoize countAffordable — раньше итерировался по
  // всему UPGRADES_ALPHA (32 entries) на каждый render (= каждый tap).
  // Теперь пересчитывается только когда покупки/эпоха/баланс меняются.
  const affordable = useMemo(
    () => countAffordable(purchased, epoch, awareness, spiritBalls),
    [purchased, epoch, awareness, spiritBalls],
  );

  function handleOpen(): void {
    setOpen(true);
    setOpenedAt(Date.now());
    emit('upgrade.panel_opened', {});
  }

  function handleClose(): void {
    setOpen(false);
    if (openedAt !== null) {
      emit('upgrade.panel_closed', { duration_ms: Date.now() - openedAt });
    }
  }

  const showBadge = !open && (affordable.awareness > 0 || affordable.spiritBalls > 0);

  return (
    <>
      <button
        ref={openerAnchor}
        type="button"
        className={styles.opener}
        onClick={open ? handleClose : handleOpen}
        aria-expanded={open}
        aria-controls="upgrades-panel"
      >
        {open ? 'свернуть' : 'апгрейды'}
        {showBadge && (
          <span
            className={styles.badgeRow}
            aria-label={`доступно: ${affordable.awareness.toString()} за осознания, ${affordable.spiritBalls.toString()} за духобаллы`}
          >
            {affordable.awareness > 0 && (
              <span className={`${styles.badge ?? ''} ${styles.badgeAwareness ?? ''}`}>
                {affordable.awareness.toString()}
              </span>
            )}
            {affordable.spiritBalls > 0 && (
              <span className={`${styles.badge ?? ''} ${styles.badgeSpirit ?? ''}`}>
                {affordable.spiritBalls.toString()}
              </span>
            )}
          </span>
        )}
      </button>
      {open && <UpgradesPanelBody onClose={handleClose} />}
    </>
  );
}

/**
 * Подсчёт апгрейдов, которые игрок может купить ПРЯМО СЕЙЧАС, разбитых
 * по валюте оплаты. Возвращает {awareness, spiritBalls} — каждое = число
 * unlocked апгрейдов где balance ≥ next-level cost.
 */
function countAffordable(
  purchased: ReadonlyMap<string, number>,
  epoch: Epoch,
  awareness: number,
  spiritBalls: number,
): { awareness: number; spiritBalls: number } {
  let aw = 0;
  let sb = 0;
  for (const u of UPGRADES_ALPHA) {
    if (!isUpgradeUnlocked(u, epoch, purchased)) continue;
    const level = getUpgradeLevel(purchased, u.id);
    if (level >= u.maxLevel) continue;
    const cost = computeUpgradeCost(u, level);
    if (u.currency === 'spirit_balls') {
      if (spiritBalls >= cost) sb += 1;
    } else {
      if (awareness >= cost) aw += 1;
    }
  }
  return { awareness: aw, spiritBalls: sb };
}

interface BodyProps {
  readonly onClose: () => void;
}

/**
 * Табы группируют апгрейды по целевой механике (то что игрок хочет
 * улучшить), а не по валюте. Внутри каждого таба видны и осознанья-ные,
 * и духобалл-ные карточки.
 *
 * JB 2026-05-26: «может апгрейды все таки как то по табам распределить
 * а то листать каждый раз длиннющий список».
 */
type TabKey = 'tap' | 'rhythm' | 'memory' | 'guide';

interface TabDef {
  readonly key: TabKey;
  readonly label: string;
  readonly icon: string;
  /** Какие category Upgrade попадают в этот таб. */
  readonly categories: readonly Upgrade['category'][];
}

const TABS: readonly TabDef[] = [
  { key: 'tap', label: 'тап', icon: '◉', categories: ['tap'] },
  { key: 'rhythm', label: 'ритм', icon: '◌', categories: ['rhythm'] },
  // memory + building → «память» (Алхимия кристалла + Беседка Фрактал —
  // обе про укрепление пути)
  { key: 'memory', label: 'память', icon: '✧', categories: ['memory', 'building'] },
  { key: 'guide', label: 'проводники', icon: '◈', categories: ['guide'] },
];

/**
 * Per-upgrade иконка-символ для карточки. JB 2026-05-26: «всему надо
 * иконки придумать». Подбираем unicode glyph под смысл каждого апгрейда
 * — солнце для светлой искры, концентрические круги для глубокого тапа
 * и т.п. Фолбэк по category, если id не найден.
 */
const UPGRADE_ICONS: Readonly<Record<string, string>> = {
  // Tap multipliers
  u001_bright_spark: '☀',
  u002_deep_tap: '⊛',
  u010_voice_of_sense: '❀',
  u030_winged_atlas: '✦',
  u011_word_root: '⌬',
  u012_deep_awareness: '✧',
  // Rhythm
  u003_finger_resonance: '◌',
  u021_purity_chain: '⟢',
  // Memory + building
  u040_crystal_alchemy: '⨀',
  b010_fractal_pavilion: '⌬',
  // Guides
  b001_silence_keeper: '◉',
  b002_silence_keeper_2: '◉',
  b003_alchemist: '◈',
};

const CATEGORY_FALLBACK_ICONS: Readonly<Record<Upgrade['category'], string>> = {
  tap: '◉',
  rhythm: '◌',
  memory: '✧',
  guide: '◈',
  building: '⌬',
  unlock: '◐', // Открытия (для будущих unlock-апгрейдов)
  layer: '◑', // Layer-эффекты (если появятся отдельно)
};

function iconFor(upgrade: Upgrade): string {
  return UPGRADE_ICONS[upgrade.id] ?? CATEGORY_FALLBACK_ICONS[upgrade.category];
}

function UpgradesPanelBody({ onClose }: BodyProps): JSX.Element {
  const purchased = useGameStore((s) => s.state.purchasedUpgrades);
  const resources = useGameStore((s) => s.state.resources);
  const epoch = useGameStore((s) => s.state.epoch);
  const purchaseUpgrade = useGameStore((s) => s.purchaseUpgrade);
  const purchaseUpgradeMax = useGameStore((s) => s.purchaseUpgradeMax);
  const [activeTab, setActiveTab] = useState<TabKey>('tap');

  // Sort: unlocked (по epoch + base cost), затем locked.
  const sortFn = (a: Upgrade, b: Upgrade): number => {
    const aUnlocked = isUpgradeUnlocked(a, epoch, purchased) ? 0 : 1;
    const bUnlocked = isUpgradeUnlocked(b, epoch, purchased) ? 0 : 1;
    if (aUnlocked !== bUnlocked) return aUnlocked - bUnlocked;
    if (a.epoch !== b.epoch) return a.epoch - b.epoch;
    return a.baseCost - b.baseCost;
  };

  // Считаем кол-во affordable per tab — для маленьких бэйджей на табах
  // (игрок сразу видит где есть что купить).
  const affordableByTab = new Map<TabKey, number>();
  for (const tab of TABS) {
    let count = 0;
    for (const u of UPGRADES_ALPHA) {
      if (!tab.categories.includes(u.category)) continue;
      if (!isUpgradeUnlocked(u, epoch, purchased)) continue;
      const level = getUpgradeLevel(purchased, u.id);
      if (level >= u.maxLevel) continue;
      const cost = computeUpgradeCost(u, level);
      if (resources[u.currency] >= cost) count += 1;
    }
    affordableByTab.set(tab.key, count);
  }

  const activeTabDef = TABS.find((t) => t.key === activeTab) ?? TABS[0]!;
  const visibleUpgrades = UPGRADES_ALPHA.filter((u) =>
    activeTabDef.categories.includes(u.category),
  ).sort(sortFn);

  const renderCard = (upgrade: Upgrade): JSX.Element => (
    <UpgradeCard
      key={upgrade.id}
      upgrade={upgrade}
      level={getUpgradeLevel(purchased, upgrade.id)}
      isUnlocked={isUpgradeUnlocked(upgrade, epoch, purchased)}
      canAfford={
        resources[upgrade.currency] >=
        computeUpgradeCost(upgrade, getUpgradeLevel(purchased, upgrade.id))
      }
      onPurchase={() => {
        purchaseUpgrade(upgrade.id);
      }}
      onPurchaseMax={() => {
        purchaseUpgradeMax(upgrade.id);
      }}
    />
  );

  return (
    <section id="upgrades-panel" className={styles.panel} aria-label="Магазин апгрейдов">
      <header className={styles.header}>
        <h2 className={styles.title}>память пути</h2>
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Закрыть">
          ✕
        </button>
      </header>
      <div className={styles.resources}>
        <span>осознаний: {formatNumber(resources.awarenesses)}</span>
        <span className={styles.resourceGold}>
          духобаллов: {formatNumber(resources.spirit_balls)}
        </span>
      </div>

      {/* Табы по целевой механике. */}
      <div className={styles.tabBar} role="tablist" aria-label="Категории апгрейдов">
        {TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          const tabClass = `${styles.tab ?? ''} ${isActive ? (styles.tabActive ?? '') : ''}`;
          const count = affordableByTab.get(tab.key) ?? 0;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={tabClass}
              onClick={() => {
                setActiveTab(tab.key);
              }}
            >
              <span className={styles.tabIcon} aria-hidden>
                {tab.icon}
              </span>
              <span className={styles.tabLabel}>{tab.label}</span>
              {count > 0 && (
                <span className={styles.tabCount} aria-label={`${count.toString()} доступно`}>
                  {count.toString()}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <ul className={styles.list}>
        {visibleUpgrades.length > 0 ? (
          visibleUpgrades.map(renderCard)
        ) : (
          <li className={styles.emptyTab}>здесь пока пусто. откроется позже.</li>
        )}
      </ul>
    </section>
  );
}

interface CardProps {
  readonly upgrade: Upgrade;
  readonly level: number;
  readonly isUnlocked: boolean;
  readonly canAfford: boolean;
  readonly onPurchase: () => void;
  readonly onPurchaseMax: () => void;
}

function UpgradeCard({
  upgrade,
  level,
  isUnlocked,
  canAfford,
  onPurchase,
  onPurchaseMax,
}: CardProps): JSX.Element {
  const atMax = level >= upgrade.maxLevel;
  const cost = computeUpgradeCost(upgrade, level);
  const currencyLabel = upgrade.currency === 'spirit_balls' ? 'духобаллов' : 'осознаний';
  const effect = describeUpgradeEffect(upgrade, level);
  const cardClass = [
    styles.card ?? '',
    !isUnlocked ? (styles.cardLocked ?? '') : '',
    atMax ? (styles.cardMax ?? '') : '',
  ]
    .filter(Boolean)
    .join(' ');

  const lockHint = (() => {
    if (atMax) return 'предел достигнут';
    if (!isUnlocked) {
      if (upgrade.requires !== undefined) {
        return `требует «${upgrade.requires.upgradeId}» ур. ${upgrade.requires.minLevel.toString()}`;
      }
      return `откроется в эпохе ${upgrade.epoch.toString()}`;
    }
    return null;
  })();

  return (
    <li className={cardClass}>
      <div className={styles.cardHead}>
        <span
          className={`${styles.cardIcon ?? ''} ${styles[`cardIcon_${upgrade.category}`] ?? ''}`}
          aria-hidden
        >
          {iconFor(upgrade)}
        </span>
        <span className={styles.cardName}>{upgrade.name}</span>
        {level > 0 && (
          <span className={styles.cardLevel}>
            ур. {level.toString()}
            {upgrade.maxLevel > 1 ? ` / ${upgrade.maxLevel.toString()}` : ''}
          </span>
        )}
      </div>
      <p className={styles.cardDesc}>{upgrade.description}</p>
      {/* Math line — what the upgrade currently gives + what the next level
          will add. JB 2026-05-25: «пользователь должен понимать что апгрейды
          делают математически геймлейно». */}
      {effect.currentLabel !== null && (
        <p className={styles.cardEffectCurrent}>{effect.currentLabel}</p>
      )}
      {effect.nextLabel !== null && <p className={styles.cardEffectNext}>{effect.nextLabel}</p>}
      <p className={styles.cardVisible}>· {upgrade.visibleChange.description}</p>
      {lockHint !== null ? (
        <p className={styles.cardLock}>{lockHint}</p>
      ) : (
        <div className={styles.buyRow}>
          <button
            type="button"
            className={styles.buyButton}
            onClick={onPurchase}
            disabled={!canAfford}
            aria-label={`Купить ${upgrade.name} за ${cost.toString()} ${currencyLabel}`}
          >
            {formatNumber(cost)} {currencyLabel}
          </button>
          {/* Bulk-buy: показываем «× max» только когда maxLevel > 1
              (single-level upgrades типа Хранителя — мax бесполезен),
              upgrade пока не на потолке, и игрок может позволить хотя
              бы первый. JB 2026-05-26 doc 28 P2: late-game click-spam. */}
          {upgrade.maxLevel > 1 && level < upgrade.maxLevel && canAfford && (
            <button
              type="button"
              className={styles.buyMaxButton}
              onClick={onPurchaseMax}
              aria-label={`Купить ${upgrade.name} максимум доступных уровней`}
              title="купить максимум за один тап"
            >
              × max
            </button>
          )}
        </div>
      )}
    </li>
  );
}
